<template>
  <div class="pointer-events-none fixed z-50 h-svh w-full">
    <!-- Fixed Header -->
    <div
      class="pointer-events-auto relative z-50 h-header w-full select-none border-b-8 border-skblue bg-white"
    >
      <div class="mx-auto flex h-full max-w-content">
        <div
          class="flex w-full items-center justify-between py-4 pl-sidespacing pr-sidespacing s:pr-8"
        >
          <!-- Header left -->
          <a href="/" aria-label="Startseite">
            <img
              v-if="headerImg"
              class="h-header-image object-contain"
              :src="headerImg"
              alt="header logo"
            />
            <span v-else class="text-2xl">Home</span>
          </a>

          <!-- Header right -->
          <div class="flex items-center gap-8">
            <nav aria-label="Hauptnavigation">
              <!-- Desktop Nav Super Groups -->
              <div class="flex gap-8 m:hidden">
                <button
                  type="button"
                  v-for="(superGroup, i) in navLinks"
                  :class="[
                    'cursor-pointer text-base',
                    'underline underline-offset-8',
                    'transition-all duration-300 hover:decoration-primary',
                    activeSuperIndex == i && isOpen
                      ? 'decoration-primary'
                      : 'decoration-transparent hover:scale-110',
                  ]"
                  :aria-expanded="activeSuperIndex == i && isOpen"
                  aria-haspopup="true"
                  aria-controls="primary-nav-desktop"
                  @click="openMenuAtIndex(i)"
                >
                  {{ superGroup.heading }}
                </button>
              </div>

              <!-- Mobile Hamburger Button -->
              <button
                type="button"
                class="flex min-h-11 min-w-11 cursor-pointer items-center justify-center minM:hidden"
                :aria-expanded="isOpen"
                aria-controls="primary-nav-mobile"
                :aria-label="isOpen ? 'Menü schliessen' : 'Menü öffnen'"
                @click="toggleMenu"
              >
                <span class="flex h-5 w-6 flex-col justify-between">
                  <span
                    v-for="n in 3"
                    :key="n"
                    class="h-0.5 w-full rounded-full bg-black transition-transform duration-300"
                    :class="{
                      'translate-y-[9px] rotate-45': isOpen && n === 1,
                      'scale-x-0': isOpen && n === 2,
                      '-translate-y-[9px] -rotate-45': isOpen && n === 3,
                    }"
                  />
                </span>
              </button>
            </nav>
            <div class="hidden items-baseline gap-4 md:flex">
              <a
                class="p-2 transition-transform ease-in-out hover:scale-125"
                href="/search"
                aria-label="Suche"
                ><i class="fa-light fa-search" aria-hidden="true"></i
              ></a>
              <a
                :href="headerButton?.url"
                :target="headerButton?.target"
                class="button flex items-baseline gap-3"
              >
                <i
                  class="fa-light"
                  :class="headerButton?.icon"
                  aria-hidden="true"
                ></i>
                <span class="text-base">{{ headerButton?.text }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Slide Menu -->
    <div class="pointer-events-auto relative">
      <div
        id="primary-nav-desktop"
        class="absolute z-40 max-h-[calc(100svh-theme('spacing.header'))] w-full transform overflow-y-auto bg-white transition-transform duration-300 ease-in-out m:hidden"
        :class="isOpen ? 'translate-y-0' : '-translate-y-full'"
        :inert="!isOpen"
        :aria-hidden="!isOpen"
      >
        <div class="relative mx-auto max-w-content">
          <div
            :key="activeSuperIndex"
            class="flex w-full flex-row gap-12 px-sidespacing py-16"
          >
            <!-- Group Headings -->
            <div class="min-w-1/3 flex grow flex-col gap-8">
              <component
                :is="activeNavGroupIndex == i ? 'a' : 'button'"
                :type="activeNavGroupIndex == i ? undefined : 'button'"
                :href="activeNavGroupIndex == i ? navGroup.link : undefined"
                class="w-fit cursor-pointer text-start underline decoration-4 underline-offset-8 transition"
                v-for="(navGroup, i) in navGroups"
                :class="
                  activeNavGroupIndex == i
                    ? 'text-primary decoration-primary'
                    : 'decoration-transparent hover:decoration-primary'
                "
                :aria-expanded="activeNavGroupIndex == i"
                @click="selectNavGroup(i)"
              >
                <span class="text-4xl font-extrabold">{{
                  navGroup.heading
                }}</span>
              </component>
            </div>

            <!-- Sub Menus -->
            <div class="grid w-2/3 grid-cols-3 gap-x-10 gap-y-16">
              <div v-for="subMenu in activeNavGroup.subMenus">
                <div class="flex gap-4">
                  <!-- Single Link -->
                  <a
                    v-if="subMenu.singleLink"
                    :href="subMenu.singleLink"
                    class="flex items-baseline pr-2"
                  >
                    <i
                      class="fa-light size-8 text-xl"
                      v-if="subMenu.icon"
                      :class="subMenu.icon"
                      aria-hidden="true"
                    ></i>
                    <span
                      class="pt-1 text-base font-bold underline decoration-transparent decoration-2 underline-offset-8 transition duration-300 ease-in-out hover:translate-x-2 hover:decoration-primary"
                      >{{ subMenu.heading }}</span
                    >
                  </a>
                  <!-- Link Group Heading -->
                  <div v-else class="flex items-baseline pr-2">
                    <i
                      class="fa-light size-8 text-xl"
                      v-if="subMenu.icon"
                      :class="subMenu.icon"
                      aria-hidden="true"
                    ></i>
                    <div>
                      <a
                        :href="subMenu.directLink || undefined"
                        class="ml-0 pt-1 text-base font-bold"
                        :class="{
                          'underline decoration-transparent decoration-2 underline-offset-8 transition-all duration-300 ease-in-out hover:ml-2 hover:decoration-primary':
                            subMenu.directLink,
                        }"
                      >
                        {{ subMenu.heading }}
                      </a>
                      <!-- Link Group Links-->
                      <div class="mt-2 flex flex-col">
                        <a
                          v-for="link in subMenu.links"
                          :href="link.url"
                          class="link py-2 text-sm"
                        >
                          <span class="link-text font-normal text-black">{{
                            link.text
                          }}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Slide Menu -->
    <div class="pointer-events-auto relative">
      <div
        id="primary-nav-mobile"
        class="absolute z-40 h-[calc(100svh-theme('spacing.header'))] w-full transform overflow-y-auto bg-white transition-transform duration-300 ease-in-out minM:hidden"
        :class="isOpen ? 'translate-y-0' : '-translate-y-full'"
        :inert="!isOpen"
        :aria-hidden="!isOpen"
      >
        <div class="mx-auto h-full max-w-content">
          <div class="relative flex h-full flex-col gap-12 pb-32 pt-8">
            <div>
              <Transition
                :name="mobileTransitionName"
                @before-leave="onBeforeLeave"
              >
                <div :key="mobileNavLevel" class="flex w-full flex-col">
                  <!-- Super Headings -->
                  <button
                    type="button"
                    v-if="mobileNavLevel === 0"
                    v-for="(superGroup, i) in navLinks"
                    class="flex min-h-11 w-fit items-center px-sidespacing py-3 text-start first:pt-0 last:pb-0"
                    @click="mobileEnterSuperGroup(i)"
                  >
                    <span class="overpass-f4 font-extrabold">{{
                      superGroup.heading
                    }}</span>
                  </button>

                  <!-- back buttons -->
                  <div v-if="mobileNavLevel > 0">
                    <div class="flex justify-between px-sidespacing">
                      <button
                        type="button"
                        @click="mobileGoBack()"
                        class="-translate-x-4 -translate-y-4 p-4"
                      >
                        <i
                          class="fa-light fa-chevron-left mr-2 text-xs"
                          aria-hidden="true"
                        ></i>
                        <span class="text-base">zurück</span>
                      </button>
                      <button
                        type="button"
                        @click="mobileGoTo(0)"
                        class="-translate-y-4 translate-x-4 p-4"
                      >
                        <span class="text-base">{{
                          navLinks[activeSuperIndex].heading
                        }}</span>
                        <i class="fa-light fa-home ml-2" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>

                  <!-- nav groups level mobile nav -->
                  <div v-if="mobileNavLevel == 1">
                    <div v-for="(_, i) in navLinks" class="flex flex-col gap-8">
                      <div v-if="activeSuperIndex == i" class="flex flex-col">
                        <button
                          type="button"
                          v-for="(navGroup, ii) in navLinks[i].navGroups"
                          class="flex min-h-11 w-fit items-center p-4 px-sidespacing pr-4 text-start"
                          @click="mobileEnterNavGroup(ii)"
                        >
                          <span class="overpass-f4 font-extrabold">{{
                            navGroup.heading
                          }}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- sub menus level mobile nav -->
                  <div v-if="mobileNavLevel == 2" class="flex flex-col">
                    <!-- Nav Group Header -->
                    <div class="px-sidespacing">
                      <span
                        class="overpass-f4 font-extrabold text-primary underline underline-offset-8"
                        >{{ activeNavGroup.heading }}</span
                      >
                    </div>

                    <div
                      v-for="(subMenu, i) in activeNavGroup.subMenus"
                      class="flex h-fit flex-col"
                    >
                      <!-- Single Link sub menu -->
                      <a
                        v-if="subMenu.singleLink"
                        :href="subMenu.singleLink"
                        class="flex min-h-11 w-fit items-center px-sidespacing py-4"
                      >
                        <i
                          class="fa-light w-8"
                          :class="subMenu.icon"
                          aria-hidden="true"
                        ></i>
                        <span class="text-base font-bold">{{
                          subMenu.heading
                        }}</span>
                      </a>

                      <!-- Link Group sub menu -->
                      <div v-else class="px-sidespacing">
                        <!-- sub menu Heading: a real link once expanded (when
                             it has a direct target), otherwise an expander
                             button — never a link nested inside a button -->
                        <a
                          v-if="activeSubMenuIndex == i && subMenu.directLink"
                          :href="subMenu.directLink"
                          class="flex min-h-11 w-full items-center py-4 text-start"
                        >
                          <i
                            class="fa-light w-8"
                            :class="subMenu.icon"
                            aria-hidden="true"
                          ></i>
                          <span class="text-base font-bold">{{
                            subMenu.heading
                          }}</span>
                        </a>
                        <button
                          v-else
                          type="button"
                          class="flex min-h-11 w-full items-center py-4 text-start"
                          :aria-expanded="activeSubMenuIndex == i"
                          :aria-controls="`hamburger-submenu-${i}`"
                          @click="setActiveSubMenu(i)"
                        >
                          <i
                            class="fa-light w-8"
                            :class="subMenu.icon"
                            aria-hidden="true"
                          ></i>
                          <span class="text-base font-bold">{{
                            subMenu.heading
                          }}</span>
                        </button>

                        <!-- sub menu Items: inert while collapsed so the
                             visually hidden links can't take keyboard focus -->
                        <div
                          class="flex flex-col"
                          :id="`hamburger-submenu-${i}`"
                          :inert="activeSubMenuIndex != i"
                        >
                          <a
                            class="relative transition-all duration-300"
                            :class="
                              activeSubMenuIndex == i
                                ? 'pointer-events-auto h-full px-8 py-6 opacity-100'
                                : 'pointer-events-none h-[0%] p-0 opacity-0'
                            "
                            v-for="link in subMenu.links"
                            :href="link.url"
                          >
                            <span
                              class="absolute inset-0 left-8 flex items-center text-f6"
                              >{{ link.text }}</span
                            >
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
            <div
              class="z-10 mt-auto flex flex-col gap-2 bg-white px-sidespacing pb-8"
            >
              <a
                :href="headerButton?.url"
                :target="headerButton?.target"
                class="button w-full items-baseline justify-center gap-4"
              >
                <i class="fa-light fa-lock" aria-hidden="true"></i
                ><span>{{ headerButton?.text }}</span>
              </a>
              <a
                href="/search"
                class="button_secondary w-full items-baseline justify-center gap-4"
              >
                <i class="fa-light fa-search" aria-hidden="true"></i>
                <span>Suche</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Background overlay -->
    <div
      class="fixed inset-0 z-30 cursor-pointer bg-black transition-colors duration-300"
      :class="isOpen ? 'pointer-events-auto bg-opacity-50' : 'bg-opacity-0'"
      @click="closeMenu"
      aria-hidden="true"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { trackEvent } from '../utils/tracking';
import { lockScroll, unlockScroll } from '../utils/scrollLock';

interface NavLink {
  heading: string;
  navGroups: NavGroup[];
}

interface NavGroup {
  heading: string;
  link?: string;
  subMenus: [
    {
      icon?: string;
      heading: string;
      links: {
        url: string;
        text: string;
      }[];
      directLink?: string;
      singleLink?: string;
    },
  ];
}

interface HeaderButton {
  url: string;
  target: string;
  icon: string;
  text?: string;
}

const { headerImg, navLinks, headerButton, warning } = defineProps<{
  headerImg: string;
  navLinks: NavLink[];
  headerButton?: HeaderButton;
  warning: {
    text?: string;
    url?: string;
  };
}>();

const activeSuperIndex = ref(0);
const activeNavGroupIndex = ref(0);
const activeSubMenuIndex = ref(0);
const isOpen = ref(false);

// Track main-navigation open/close from one place — covers the burger toggle
// and the desktop super-nav, which both flip isOpen.
watch(isOpen, (open) => {
  trackEvent('Navigation', open ? 'Open Menu' : 'Close Menu');
});
const mobileNavLevel = ref(0);
const mobileTransitionName = ref('slide-next');
const setBodyState = (open: boolean) => {
  if (open) {
    lockScroll();
  } else {
    unlockScroll();
  }
};

const lastTrigger = ref<HTMLElement | null>(null);

watch(isOpen, (open) => {
  setBodyState(open);
  if (open) {
    // Remember what opened the menu so focus can return there on close.
    lastTrigger.value = document.activeElement as HTMLElement | null;
  } else if (lastTrigger.value) {
    // Closing: move focus out of the now-inert menu, back to its trigger.
    lastTrigger.value.focus();
    lastTrigger.value = null;
  }
});

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    closeMenu();
  }
};

onMounted(() => {
  if (isOpen.value) {
    setBodyState(true);
  }
  window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
});

const navGroups = computed((): NavGroup[] => {
  return navLinks[activeSuperIndex.value].navGroups;
});

const activeNavGroup = computed((): NavGroup => {
  return navGroups.value[activeNavGroupIndex.value];
});

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
};

const closeMenu = () => {
  isOpen.value = false;
};

const openMenuAtIndex = (newSuperIndex: number) => {
  if (isOpen.value && activeSuperIndex.value === newSuperIndex) {
    isOpen.value = false;
  } else {
    setActiveNavGroup(0); // reset nav group index to avoid getting out of range
    setActiveSuperIndex(newSuperIndex);
    isOpen.value = true;
  }
};

const setActiveSuperIndex = (newSuperIndex: number) => {
  activeSuperIndex.value = newSuperIndex;
};

const setActiveNavGroup = (newNavGroupIndex: number) => {
  activeNavGroupIndex.value = newNavGroupIndex;
};

// Wrapper for the user-facing nav-group click (template). The programmatic
// resets that also call setActiveNavGroup stay untracked, so analytics only
// records deliberate selections.
const selectNavGroup = (newNavGroupIndex: number) => {
  setActiveNavGroup(newNavGroupIndex);
  trackEvent(
    'Navigation',
    'Select Group',
    navGroups.value[newNavGroupIndex]?.heading,
  );
};

const setActiveSubMenu = (newSubMenuIndex: number) => {
  activeSubMenuIndex.value = newSubMenuIndex;
};

const mobileGoTo = (newMobileNavLevel: number) => {
  if (newMobileNavLevel > mobileNavLevel.value) {
    mobileTransitionName.value = 'slide-next';
  } else if (newMobileNavLevel < mobileNavLevel.value) {
    mobileTransitionName.value = 'slide-prev';
  }
  mobileNavLevel.value = newMobileNavLevel;
};

/** decrement active nav level by one with floor value 0 */
const mobileGoBack = () => {
  const zeroOrGreater = Math.max(mobileNavLevel.value - 1, 0);
  mobileGoTo(zeroOrGreater);
};

const mobileEnterSuperGroup = (newSuperIndex: number) => {
  setActiveSuperIndex(newSuperIndex);
  mobileGoTo(1);
  setActiveNavGroup(0);
};

const mobileEnterNavGroup = (newNavGroupIndex: number) => {
  setActiveNavGroup(newNavGroupIndex);
  mobileGoTo(2);
  setActiveSubMenu(0);
};

/** used by vue transition on mobile */
const onBeforeLeave = (el: Element) => {
  const htmlEl = el as HTMLElement;
  const { marginLeft, marginTop, width, height } =
    window.getComputedStyle(htmlEl);
  htmlEl.style.left = `${htmlEl.offsetLeft - parseFloat(marginLeft || '0')}px`;
  htmlEl.style.top = `${htmlEl.offsetTop - parseFloat(marginTop || '0')}px`;
  htmlEl.style.width = width;
  htmlEl.style.height = height;
  htmlEl.style.position = 'absolute';
};
</script>

<style lang="scss">
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: all 300ms ease-in-out;
}

.slide-next-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-next-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-prev-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-prev-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
